import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { fetchMe, loginStaff, logout as logoutRequest, refreshTokens, verifyStudentLoginCode, type LoginPayload } from "@/api/auth";
import {
  clearTokens,
  getRefreshToken,
  onUnauthorized,
  setAccessToken,
  setRefreshToken,
} from "@/api/tokenStore";
import type { AuthenticatedUser, LoginResponse } from "@/types/user";
import type { PermissionKey } from "@/types/permissions";

interface AuthContextValue {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasPermission: (key: PermissionKey) => boolean;
  hasAnyPermission: (keys: PermissionKey[]) => boolean;
  loginWithPassword: (payload: LoginPayload) => Promise<AuthenticatedUser>;
  loginWithStudentCode: (phone: string, code: string) => Promise<AuthenticatedUser>;
  applyLoginResponse: (response: LoginResponse) => void;
  logout: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const applyLoginResponse = useCallback((response: LoginResponse) => {
    setAccessToken(response.accessToken);
    setRefreshToken(response.refreshToken);
    setUser(response.user);
  }, []);

  const clearSession = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  const loginWithPassword = useCallback(
    async (payload: LoginPayload) => {
      const response = await loginStaff(payload);
      applyLoginResponse(response);
      return response.user;
    },
    [applyLoginResponse],
  );

  const loginWithStudentCode = useCallback(
    async (phone: string, code: string) => {
      const response = await verifyStudentLoginCode(phone, code);
      applyLoginResponse(response);
      return response.user;
    },
    [applyLoginResponse],
  );

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    try {
      if (refreshToken) await logoutRequest(refreshToken);
    } catch {
      // Игнорируем ошибку logout на backend — локальную сессию всё равно очищаем.
    } finally {
      clearSession();
    }
  }, [clearSession]);

  useEffect(() => {
    onUnauthorized(() => {
      clearSession();
    });
  }, [clearSession]);

  // Попытка восстановить сессию при загрузке страницы по refresh-токену из localStorage
  // (см. комментарий о хранении токенов в src/api/tokenStore.ts).
  useEffect(() => {
    let active = true;
    async function restore() {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        setIsLoading(false);
        return;
      }
      try {
        const tokens = await refreshTokens(refreshToken);
        setAccessToken(tokens.accessToken);
        setRefreshToken(tokens.refreshToken);
        const me = await fetchMe();
        if (active) setUser(me);
      } catch {
        clearSession();
      } finally {
        if (active) setIsLoading(false);
      }
    }
    void restore();
    return () => {
      active = false;
    };
  }, [clearSession]);

  const hasPermission = useCallback((key: PermissionKey) => Boolean(user?.permissions?.includes(key)), [user]);
  const hasAnyPermission = useCallback(
    (keys: PermissionKey[]) => keys.some((key) => user?.permissions?.includes(key)),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: Boolean(user),
      hasPermission,
      hasAnyPermission,
      loginWithPassword,
      loginWithStudentCode,
      applyLoginResponse,
      logout,
    }),
    [user, isLoading, hasPermission, hasAnyPermission, loginWithPassword, loginWithStudentCode, applyLoginResponse, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
